import React, { createContext, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Assignee,
  BluePrint,
  CalendarActivity,
  CalendarActivityType,
  DragActivity,
  GroupBy,
  Reshaping,
  SelectedActivity,
  SelectedActivityState,
  TimeScale,
  ViewType,
  WorkingHours,
} from 'utilities/Planning';
import {
  ActivityType,
  Availability,
  ActivityTypeDefaultEnum,
  Contact,
  ContactDetail,
  DailyNote,
  DayPartStartTime,
  Facility,
  FacilityEvent,
  Horse,
  HorseGroup,
  PublicHorse,
  RealActivities,
  Role,
  ShallowContact,
  Stable,
  WorkDayAvailability,
  WorkDayAvailabilityManager,
} from 'openapi';
import { ApiPromises } from 'utilities/ApiPromises';
import { useAccount } from './AccountContext';
import { addDays, endOfDay, isSameDay } from 'date-fns';
import { GroupByApplied } from './Calendar';
import { FALLBACK_ACTIVITY_PRIMARY_COLOR, FALLBACK_ACTIVITY_SECONDARY_COLOR } from 'ui/Const';
import { ApiErrorParserBase } from 'api/ApiErrorParser';
import { useOrganization } from './OrganizationContext';
import { parseTime, timeSmallerThan } from 'utilities/date.utilities';
import shadeBlendColor from 'shade-blend-color';
import { availabilityTypeColor } from 'utilities/HR';

interface Props {
  children: React.ReactNode;
  viewType?: ViewType;
  timeScale?: TimeScale;
  groupBy?: GroupBy;
  horses?: Horse[];
  filterHorseUid?: string;
  fixedActivityType?: ActivityTypeDefaultEnum;
}

type PlanningContextType = {
  viewType: ViewType;
  timeScale: TimeScale;
  groupBy: GroupBy;
  filterHorseUid?: string;
  fixedActivityType: ActivityTypeDefaultEnum | undefined;
  apiPromises: ApiPromises | undefined;
  activities: RealActivities[] | undefined;
  dailyNotes: DailyNote[] | undefined;
  dayParts: DayPartStartTime[] | undefined;
  horses: Horse[] | undefined;
  publicHorses: PublicHorse[] | undefined;
  contacts: Contact[] | undefined;
  activityTypes: ActivityType[] | undefined;
  groups: HorseGroup[] | undefined;
  stables: Stable[] | undefined;
  roles: Role[] | undefined;
  facilities: Facility[] | undefined;
  facilityEvents: FacilityEvent[] | undefined;
  lastUsedActivityTypeUid: string | undefined;
  historicOffset: { current: number; previous?: number };
  setViewType: (view: ViewType) => void;
  setTimeScale: (time: TimeScale) => void;
  setGroupBy: (group: GroupBy) => void;
  setApiPromises: (api: ApiPromises) => void;
  setActivities: Dispatch<SetStateAction<RealActivities[] | undefined>> | Dispatch<SetStateAction<RealActivities[]>>;
  setDailyNotes: Dispatch<SetStateAction<DailyNote[] | undefined>> | Dispatch<SetStateAction<DailyNote[]>>;
  setDayParts: Dispatch<SetStateAction<DayPartStartTime[] | undefined>> | Dispatch<SetStateAction<DayPartStartTime[]>>;
  setHorses: Dispatch<SetStateAction<Horse[] | undefined>> | Dispatch<SetStateAction<Horse[]>>;
  setPublicHorses: Dispatch<SetStateAction<PublicHorse[] | undefined>> | Dispatch<SetStateAction<PublicHorse[]>>;
  setContacts: Dispatch<SetStateAction<Contact[] | undefined>> | Dispatch<SetStateAction<Contact[]>>;
  setContactsShallow: Dispatch<SetStateAction<ShallowContact[] | undefined>> | Dispatch<SetStateAction<ShallowContact[]>>;
  updateContact: (contact: Contact) => void;
  setActivityTypes: Dispatch<SetStateAction<ActivityType[] | undefined>> | Dispatch<SetStateAction<ActivityType[]>>;
  setGroups: Dispatch<SetStateAction<HorseGroup[] | undefined>> | Dispatch<SetStateAction<HorseGroup[]>>;
  setStables: Dispatch<SetStateAction<Stable[] | undefined>> | Dispatch<SetStateAction<Stable[]>>;
  setFacilities: Dispatch<SetStateAction<Facility[] | undefined>> | Dispatch<SetStateAction<Facility[]>>;
  setFacilityEvents: Dispatch<SetStateAction<FacilityEvent[] | undefined>> | Dispatch<SetStateAction<FacilityEvent[]>>;
  setRoles: Dispatch<SetStateAction<Role[] | undefined>> | Dispatch<SetStateAction<Role[]>>;
  setLastUsedActivityTypeUid: (activityTypeUid: string) => void;
  setHistoricOffset: (offset: { current: number; previous?: number }) => void;
  processedActivities: CalendarActivity[] | undefined;
  bluePrint: BluePrint | undefined;
  reshaping: Reshaping | undefined;
  requestBluePrint: (bluePrint?: BluePrint) => void;
  requestReshaping: (reshaping?: Reshaping) => void;

  /**
   * Set a selected activity
   *
   * @param groupByUid In the Staff planning, the same activity can be displayed more than once when we assign multiple
   * contacts to it. Therefor we can specify for which contact the activity is selected.
   */
  setSelectedActivity: (
    activity: CalendarActivity,
    state: SelectedActivityState,
    day: Date,
    groupByUid?: string,
    target?: HTMLElement,
  ) => void;
  clearSelectedActivity: () => void;
  selectedActivity: SelectedActivity | undefined;

  setDragActivity: (activity?: DragActivity) => void;
  dragActivity: DragActivity | undefined;

  // Users can click on a specific horse/contact. It will be expanded into a 24h time view.
  setSelectedGroupBy: (appliedGroupBy?: GroupByApplied) => void;
  selectedGroupBy: GroupByApplied | undefined;

  setModalError: (errorMessage: string | ApiErrorParserBase | undefined) => void;
  modalError: string | undefined;

  // The working hours so we can hide the nightly hours in our timescale view.
  workingHours: WorkingHours;
  hideNonWorkingHours: boolean;
  setHideNonWorkingHours: (hide: boolean) => void;

  inspectActivitiesInModal: CalendarActivity[] | undefined;
  setInspectActivitiesInModal: Dispatch<SetStateAction<CalendarActivity[] | undefined>> | Dispatch<SetStateAction<CalendarActivity[]>>;
  clearInspectActivitiesInModal: () => void;

  setWorkDayAvailabilityActivitiesManager:
    | Dispatch<SetStateAction<WorkDayAvailabilityManager[] | undefined>>
    | Dispatch<SetStateAction<WorkDayAvailabilityManager[]>>;
  workDayAvailabilityActivitiesManager: WorkDayAvailabilityManager[] | undefined;

  setWorkDayAvailabilityActivities:
    | Dispatch<SetStateAction<WorkDayAvailability[] | undefined>>
    | Dispatch<SetStateAction<WorkDayAvailability[]>>;
  workDayAvailabilityActivities: WorkDayAvailability[] | undefined;

  availabilities: Availability[] | undefined;
  setAvailabilities: Dispatch<SetStateAction<Availability[] | undefined>> | Dispatch<SetStateAction<Availability[]>>;
};

export const PlanningContext = createContext<PlanningContextType>({
  viewType: ViewType.Week,
  timeScale: TimeScale.DayParts,
  groupBy: GroupBy.Horse,
  fixedActivityType: undefined,
  apiPromises: undefined,
  activities: undefined,
  dailyNotes: undefined,
  dayParts: undefined,
  horses: undefined,
  publicHorses: undefined,
  contacts: undefined,
  activityTypes: undefined,
  groups: undefined,
  stables: undefined,
  facilities: undefined,
  facilityEvents: undefined,
  roles: undefined,
  lastUsedActivityTypeUid: undefined,
  historicOffset: { current: 0 },
  processedActivities: undefined,
  bluePrint: undefined,
  reshaping: undefined,
  selectedActivity: undefined,
  selectedGroupBy: undefined,
  dragActivity: undefined,
  modalError: undefined,
  workingHours: { from: { hours: 0, minutes: 0 }, to: { hours: 0, minutes: 0 } },
  hideNonWorkingHours: false,
  inspectActivitiesInModal: undefined,
  workDayAvailabilityActivities: undefined,
  workDayAvailabilityActivitiesManager: undefined,
  availabilities: undefined,
  setViewType: () => console.warn('no PlanningContext provider'),
  setTimeScale: () => console.warn('no PlanningContext provider'),
  setGroupBy: () => console.warn('no PlanningContext provider'),
  setApiPromises: () => console.warn('no PlanningContext provider'),
  setActivities: () => console.warn('no PlanningContext provider'),
  setDailyNotes: () => console.warn('no PlanningContext provider'),
  setDayParts: () => console.warn('no PlanningContext provider'),
  setHorses: () => console.warn('no PlanningContext provider'),
  setPublicHorses: () => console.warn('no PlanningContext provider'),
  setContacts: () => console.warn('no PlanningContext provider'),
  setContactsShallow: () => console.warn('no PlanningContext provider'),
  updateContact: () => console.warn('no PlanningContext provider'),
  setActivityTypes: () => console.warn('no PlanningContext provider'),
  setGroups: () => console.warn('no PlanningContext provider'),
  setStables: () => console.warn('no PlanningContext provider'),
  setFacilities: () => console.warn('no PlanningContext provider'),
  setFacilityEvents: () => console.warn('no PlanningContext provider'),
  setRoles: () => console.warn('no PlanningContext provider'),
  setLastUsedActivityTypeUid: () => console.warn('no PlanningContext provider'),
  setHistoricOffset: () => console.warn('no PlanningContext provider'),
  requestBluePrint: () => console.warn('no PlanningContext provider'),
  requestReshaping: () => console.warn('no PlanningContext provider'),
  setSelectedActivity: () => console.warn('no PlanningContext provider'),
  clearSelectedActivity: () => console.warn('no PlanningContext provider'),
  setSelectedGroupBy: () => console.warn('no PlanningContext provider'),
  setDragActivity: () => console.warn('no PlanningContext provider'),
  setModalError: () => console.warn('no PlanningContext provider'),
  setHideNonWorkingHours: () => console.warn('no PlanningContext provider'),
  setInspectActivitiesInModal: () => console.warn('no PlanningContext provider'),
  clearInspectActivitiesInModal: () => console.warn('no PlanningContext provider'),
  setAvailabilities: () => console.warn('no PlanningContext provider'),
  setWorkDayAvailabilityActivities: () => console.warn('no PlanningContext provider'),
  setWorkDayAvailabilityActivitiesManager: () => console.warn('no PlanningContext provider'),
});

export function PlanningProvider({
  children,
  viewType: initialViewType,
  timeScale: initialTimeScale,
  groupBy: initialGroupBy,
  horses: initalHorses,
  filterHorseUid,
  fixedActivityType,
}: Props): JSX.Element {
  const { formatTime, formatDateIntl, accountDetails } = useAccount();
  const { selectedOrganization } = useOrganization();

  const [viewType, setViewType] = useState<ViewType>(initialViewType ?? ViewType.Week);
  const [timeScale, setTimeScale] = useState<TimeScale>(initialTimeScale ?? TimeScale.TimeScale);
  const [groupBy, setGroupBy] = useState<GroupBy>(initialGroupBy ?? GroupBy.None);
  const [apiPromises, setApiPromises] = useState<ApiPromises>();
  const [activities, setActivities] = useState<RealActivities[] | undefined>();
  const [dailyNotes, setDailyNotes] = useState<DailyNote[] | undefined>();
  const [dayParts, setDayParts] = useState<DayPartStartTime[]>();
  const [horses, setHorses] = useState<Horse[] | undefined>(initalHorses);
  const [publicHorses, setPublicHorses] = useState<PublicHorse[]>();

  // We either have a list of full or shallow contacts.
  const [contacts, setContacts] = useState<Contact[]>();
  const [contactsShallow, setContactsShallow] = useState<ShallowContact[]>();

  const [activityTypes, setActivityTypes] = useState<ActivityType[]>();
  const [groups, setGroups] = useState<HorseGroup[]>();
  const [stables, setStables] = useState<Stable[]>();
  const [facilities, setFacilities] = useState<Facility[]>();
  const [facilityEvents, setFacilityEvents] = useState<FacilityEvent[]>();
  const [roles, setRoles] = useState<Role[]>();

  const [availabilities, setAvailabilities] = useState<Availability[]>();

  // This is used in the HR/Staff schedule. It generates the availability for the employees.
  const [workDayAvailabilityActivities, setWorkDayAvailabilityActivities] = useState<WorkDayAvailability[]>();
  const [workDayAvailabilityActivitiesManager, setWorkDayAvailabilityActivitiesManager] = useState<WorkDayAvailabilityManager[]>();

  // We keep track of which activity type is last used so we can select it by default when quickly adding new activities.
  const [lastUsedActivityTypeUid, setLastUsedActivityTypeUid] = useState<string | undefined>();

  // The view offset (0 is today). It also keeps track of the previous offset.
  const [historicOffset, setHistoricOffset] = useState<{ current: number; previous?: number }>({ current: 0 });

  // When a user clicks and drags in calendar view a blueprint is constructed. This is used to show in the gui and create an Activity from.
  const [bluePrint, setBluePrint] = useState<BluePrint | undefined>();

  // This object is set when we're making an acivity/blueprint smaller or larger based on dragging
  const [reshaping, setReshaping] = useState<Reshaping | undefined>();

  // The selected activity in the calendar
  const [selectedActivity, setSelectedActivity] = useState<SelectedActivity | undefined>();

  // This activity is set while dragging.
  const [dragActivity, setDragActivity] = useState<DragActivity | undefined>();

  // A contact/horse/etc which is selected in the planning (by clicking on it). The calender will show a detailed 24h view of this contact/horse/etc.
  const [selectedGroupBy, setSelectedGroupBy] = useState<GroupByApplied | undefined>();

  // Use this to display an error message to the user in an action modal.
  const [modalError, setModalErrorStr] = useState<string | undefined>();

  const fullOrShallowContacts = useMemo(() => {
    if (contacts) {
      return contacts;
    }
    return contactsShallow as Contact[];
  }, [contacts, contactsShallow]);

  // The working hours so we can hide the nightly hours in our timescale view.
  // Default is set to 6AM to 8PM for now, for facilities it's from 6AM to 11PM.
  const workingHours = useMemo(() => {
    if (groupBy === GroupBy.Facility || groupBy === GroupBy.FacilityAvailability) {
      return { from: { hours: 6, minutes: 0 }, to: { hours: 22, minutes: 0 } };
    } else {
      return { from: { hours: 6, minutes: 0 }, to: { hours: 20, minutes: 0 } };
    }
  }, [groupBy]);

  const [hideNonWorkingHours, setHideNonWorkingHours] = useState<boolean>(true);

  const [inspectActivitiesInModal, setInspectActivitiesInModal] = useState<CalendarActivity[] | undefined>(undefined);

  const updateContact = useCallback(
    (contact: Contact | ContactDetail) => {
      if (contacts) {
        const contactList = contacts?.filter(cont => cont.uid !== contact.uid);
        contactList?.push(contact);
        setContacts(contactList);
      }
      if (contactsShallow) {
        const contactList = contactsShallow?.filter(cont => cont.uid !== contact.uid);
        contactList?.push(contact as ShallowContact);
        setContactsShallow(contactList);
      }
    },
    [contacts, contactsShallow],
  );

  // Transform the RealActivities and DailyNotes into usable CalendarActivities the we can use in the frontend.
  const processedActivities = useMemo((): CalendarActivity[] | undefined => {
    const dayPartStartDate = (day: Date, dayPart: DayPartStartTime): Date => {
      const timeParts = dayPart.start_time.split(':');
      return new Date(day.getFullYear(), day.getMonth(), day.getDate(), Number(timeParts[0]), Number(timeParts[1]));
    };

    const getDateTimeFromDayPart = (day: Date, part: number, pos: 'start' | 'end'): Date => {
      const dayPart: DayPartStartTime | undefined = dayParts ? dayParts[part] : undefined;
      if (pos === 'start') {
        const dayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0);
        return dayPart ? dayPartStartDate(day, dayPart) : dayStart;
      } else {
        const dayPartNext: DayPartStartTime | undefined = dayParts ? dayParts[part + 1] : undefined;
        const dayEnd = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59);
        return dayPartNext ? dayPartStartDate(day, dayPartNext) : dayEnd;
      }
    };

    // Get a 'virtual' day part for where a timed event should be placed.
    const getDayPartFromStartTime = (date: Date): number => {
      if (!dayParts) {
        return 0;
      }
      for (let i = 0; i < (dayParts.length ?? 0); i++) {
        const dayPartTime = parseTime(dayParts[i].start_time);
        const time = parseTime(`${date.getHours()}:${date.getMinutes()}`);
        if (!timeSmallerThan(dayPartTime, time)) {
          return Math.max(i - 1, 0);
        }
      }
      return dayParts.length - 1;
    };

    const isFacilities = groupBy === GroupBy.Facility || groupBy === GroupBy.FacilityAvailability;

    if (isFacilities && (!facilities || !facilityEvents || !horses)) {
      // When we group by facility. We need the facilities and facility events to be loaded.
      return undefined;
    } else if (groupBy === GroupBy.EmployeeAvailability) {
      if (!availabilities) {
        return undefined;
      }
    } else if (!isFacilities && (!activities || !horses || !activityTypes)) {
      return undefined;
    }

    // Transform the FacilityEvents into usable CalendarActivities the we can use in the frontend.
    const processedFacilityEvents: CalendarActivity[] = (facilityEvents ?? []).map(facilityEvent => {
      let startTime: Date | undefined = undefined;
      let endTime: Date | undefined = undefined;
      let startEndText = '';
      if (facilityEvent.start.datetime) {
        startTime = new Date(facilityEvent.start.datetime ?? '');
        endTime = new Date(facilityEvent.end?.datetime ?? '');
        startEndText = `${formatTime(startTime)} - ${formatTime(endTime)}`;
      }

      // First search through the actual horse list, then the public horse list for the facility event horses.
      const eventHorses: (Horse | PublicHorse)[] = [];
      facilityEvent.horse_uids?.forEach(uid => {
        const found = horses?.find(horse => horse.uid === uid);
        if (found) {
          eventHorses.push(found);
        } else {
          const found = publicHorses?.find(horse => horse.uid === uid);
          if (found) {
            eventHorses.push(found);
          }
        }
      });

      const facility = facilities?.find(facility => facility.uid === facilityEvent.facility_uid);

      let contact: ShallowContact | Contact | undefined;
      if (facilityEvent.contact_uid) {
        contact = contacts?.find(contact => contact.uid === facilityEvent.contact_uid);
        if (!contact) {
          contact = contactsShallow?.find(contact => contact.uid === facilityEvent.contact_uid);
        }
        if (!contact && selectedOrganization?.me.uid === facilityEvent.contact_uid) {
          // Make a fake contact to show yourself in the facility planning.
          // This is only relevant when you don't have access to the organization contacts.
          contact = {
            uid: facilityEvent.contact_uid,
            user_uid: accountDetails?.uid ?? '',
            hidden: false,
            is_semen_collection_station: false,
            vat_number_checked: false,
            created_on: '',
            last_modified_on: '',
            stable_location_uid: '',
            manage_horse_location_history: false,
            machtiging_rvo_gegeven: '',
            lookup: '',
            machtiging_rvo_gegeven_by_uid: '',
            invitation: null,
            first_name: accountDetails?.first_name,
            last_name: accountDetails?.last_name,
          };
        }
      }
      return {
        uid: facilityEvent.uid,
        stableUid: facility?.stable_uid,
        startTime,
        endTime,
        dayPart: 0,
        startEndText,
        done: false,
        assignedTo: [], // TODO CUSTOMER ROLE
        order: -1,
        activityContactRoles: [],
        type: CalendarActivityType.FacilityEvent,
        primaryColor: FALLBACK_ACTIVITY_PRIMARY_COLOR,
        secondaryColor: FALLBACK_ACTIVITY_SECONDARY_COLOR,
        isAllDayEvent: false,
        executable: true,
        extraInfo: facilityEvent.description,
        isAutomaticallyPlanned: false,
        rRule: facilityEvent.recurrence,
        facilities: facility ? [facility] : [],
        contact: contact,
        slots: facilityEvent.slots,
        reservation_horse_uids: facilityEvent.horse_uids ?? [],
        reservation_horses: eventHorses,
        is_private_reservation: facilityEvent.is_private_reservation ?? false,
      } as CalendarActivity;
    });

    const processedAvailabilities: CalendarActivity[] = (availabilities ?? []).map(availability => {
      let startTime: Date | undefined = undefined;
      let endTime: Date | undefined = undefined;
      let startEndText = '';

      if (availability.start.datetime) {
        startTime = new Date(availability.start.datetime ?? '');
        endTime = new Date(availability.end?.datetime ?? '');
        startEndText = `${formatTime(startTime)} - ${formatTime(endTime)}`;
      } else {
        const date = new Date(availability.start.date ? availability.start.date + 'T00:00:00' : '');
        startTime = date;
        if (availability.endTimeUnspecified) {
          endTime = endOfDay(new Date());
        } else {
          endTime = addDays(endOfDay(availability.end ? new Date(availability.end.date + 'T00:00:00') : new Date()), -1);
        }
      }

      let contact: Contact | ShallowContact | undefined = contacts?.find(contact => contact.uid === availability.contact_uid);
      if (!contact) {
        contact = contactsShallow?.find(contact => contact.uid === availability.contact_uid);
      }

      return {
        uid: availability.uid,
        startTime,
        endTime,
        dayPart: -1,
        startEndText,
        done: false,
        assignedTo: [],
        order: -1,
        activityContactRoles: [],
        type: CalendarActivityType.Availability,
        primaryColor: availability.availability_type
          ? availabilityTypeColor(availability.availability_type)
          : FALLBACK_ACTIVITY_PRIMARY_COLOR,
        secondaryColor: availability.availability_type
          ? shadeBlendColor(0.7, availabilityTypeColor(availability.availability_type))
          : FALLBACK_ACTIVITY_SECONDARY_COLOR,
        isAllDayEvent: false,
        executable: true,
        extraInfo: availability.description,
        isAutomaticallyPlanned: false,
        contact,
        reservation_horse_uids: [],
        facilities: [],
        reservation_horses: [],
        is_private_reservation: false,
        availabilityType: availability.availability_type,
        availabilityStatus: availability.status,
      } as CalendarActivity;
    });

    // Transform the RealActivities into usable CalendarActivities the we can use in the frontend.
    const processedActivities: CalendarActivity[] = (activities ?? []).map(activity => {
      let dayPartIndex = 0;
      if (activity.daypart !== null && activity.daypart !== undefined) {
        dayPartIndex = activity.daypart;
        if (dayPartIndex > 0) {
          dayPartIndex = activity.daypart - 1;
        }
      }
      let startTime: Date | undefined = undefined;
      let endTime: Date | undefined = undefined;
      let startEndText = '';
      if (activity.start.datetime) {
        startTime = new Date(activity.start.datetime ?? '');
        endTime = new Date(activity.end?.datetime ?? '');
        startEndText = `${formatTime(startTime)} - ${formatTime(endTime)}`;
        dayPartIndex = getDayPartFromStartTime(startTime);
      } else {
        const date = new Date(activity.start.date ? activity.start.date + 'T00:00:00' : '');
        startTime = getDateTimeFromDayPart(date, dayPartIndex, 'start');
        endTime = getDateTimeFromDayPart(date, dayPartIndex, 'end');
        const dayPart = dayParts?.[dayPartIndex];
        startEndText = dayPart?.name ?? 'Unknown day part';
      }

      const assignedTo = (activity.activitycontactrole_set ?? []).map((contactRole): Assignee => {
        return {
          contact: fullOrShallowContacts?.find(contact => contact.uid === contactRole.contact_uid),
          role: roles?.find(role => role.uid === contactRole.role_uid),
          primary: contactRole.primary ?? false,
        };
      });
      const horse = activity.horse_uid ? (horses ?? []).find(horse => horse.uid === activity.horse_uid) : undefined;
      const activityType = (activityTypes ?? []).find(type => type.uid === activity.activity_type_uid);
      const activityFacilities = facilities?.filter(facility => activity.facility_uids?.includes(facility.uid));

      const primaryColor = activityType?.color ?? FALLBACK_ACTIVITY_PRIMARY_COLOR;

      const secondaryColor =
        (activityType?.color ? shadeBlendColor(0.7, activityType?.color) : FALLBACK_ACTIVITY_PRIMARY_COLOR) ??
        FALLBACK_ACTIVITY_PRIMARY_COLOR;

      return {
        uid: activity.uid,
        horseUid: activity.horse_uid,
        horse: horse,
        stableUid: horse?.stable_uid ?? undefined,
        startTime,
        endTime,
        activityType: activityType,
        // For dayparts we work with the index right away. So dayPart - 1.
        dayPart: dayPartIndex,
        startEndText,
        done: activity.done_on !== undefined && activity.done_on !== null,
        doneOn: activity.done_on ? new Date(activity.done_on) : undefined,
        assignedTo: assignedTo,
        order: activity.ordering ?? -1,
        activityContactRoles: activity.activitycontactrole_set ?? [],
        type: CalendarActivityType.Activity,
        primaryColor,
        secondaryColor,
        isAllDayEvent: activity.all_day_event ?? false,
        executable: true,
        extraInfo: activity.extra_info,
        pregnancyCheckTerm: activity.pregnancy_check_term,
        pregnancyCheckUid: activity.marecyclecheck_uid,
        isAutomaticallyPlanned: activity.parent_uid !== null && activity.parent_uid !== undefined,
        mareCycleCheckUid: activity.marecyclecheck_uid,
        stallionMountUid: activity.stallionmount_uid,
        rRule: activity.recurrence,
        facilities: activityFacilities ?? [],
        reservation_horse_uids: [],
        reservation_horses: [],
        is_private_reservation: false,
        breedingBoxNumber: activity.breeding_box_no ?? undefined,
      };
    });

    // Transform the DailyNotes into usable CalendarActivities the we can use in the frontend.
    const processedDailyNotes: CalendarActivity[] = (dailyNotes ?? []).map(dailyNote => {
      // For daily notes only the dailyNote.{start,end}.date should be set. But due to legacy it could
      // be that dailyNote.{start,end}.datetime is set. We ran a script for this in the backend. But
      // just to be sure, we support both.
      let startTime: Date | undefined;
      if (dailyNote.start.date) {
        startTime = new Date((dailyNote.start.date ?? '') + 'T00:00:00');
      } else if (dailyNote.start.datetime) {
        startTime = new Date(dailyNote.start.datetime);
      } else {
        startTime = new Date();
      }

      let endTime: Date | undefined;
      if (dailyNote.end?.date) {
        endTime = addDays((dailyNote.end?.date ?? '') + 'T23:59:00', -1);
      } else if (dailyNote.end?.datetime) {
        endTime = new Date(dailyNote.end.datetime);
      } else {
        endTime = new Date();
      }

      let startEndText = '';
      if (isSameDay(startTime, endTime)) {
        startEndText = formatDateIntl(startTime, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      } else {
        startEndText = `${formatDateIntl(startTime, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })} - ${formatDateIntl(endTime, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        })}`;
      }
      return {
        uid: dailyNote.uid,
        stableUid: dailyNote.stable_uid ?? undefined,
        startTime,
        endTime,
        startEndText,
        done: dailyNote.done_on !== undefined && dailyNote.done_on !== null,
        doneOn: dailyNote.done_on ? new Date(dailyNote.done_on) : undefined,
        order: dailyNote.order ?? 0,
        assignedTo: [],
        dayPart: 0,
        type: dailyNote.executable ? CalendarActivityType.Task : CalendarActivityType.Message,
        primaryColor: dailyNote.color ?? FALLBACK_ACTIVITY_PRIMARY_COLOR,
        secondaryColor: dailyNote.color ?? FALLBACK_ACTIVITY_SECONDARY_COLOR,
        title: dailyNote.title ?? undefined,
        text: dailyNote.text ?? undefined,
        isAllDayEvent: dailyNote.all_day_event ?? true,
        executable: dailyNote.executable ?? false,
        isAutomaticallyPlanned: false,
        facilities: [],
        reservation_horse_uids: [],
        reservation_horses: [],
        is_private_reservation: false,
      };
    });
    return processedActivities.concat(processedDailyNotes).concat(processedFacilityEvents).concat(processedAvailabilities);
  }, [
    activities,
    horses,
    activityTypes,
    dayParts,
    formatTime,
    formatDateIntl,
    contacts,
    contactsShallow,
    roles,
    dailyNotes,
    facilities,
    facilityEvents,
    groupBy,
    accountDetails,
    selectedOrganization,
    publicHorses,
    fullOrShallowContacts,
    availabilities,
  ]);

  const setSelectedActivityObject = useCallback(
    (activity: CalendarActivity, state: SelectedActivityState, day: Date, groupByUid?: string, target?: HTMLElement) => {
      setSelectedActivity({ activity, selectedActivityState: state, day, groupByUid, target });
    },
    [],
  );

  const clearSelectedActivity = useCallback(() => {
    setSelectedActivity(undefined);
  }, []);

  const clearInspectActivitiesInModal = useCallback(() => {
    setInspectActivitiesInModal(undefined);
  }, []);

  const requestBluePrint = useCallback((bluePrint?: BluePrint) => {
    setBluePrint(bluePrint);
  }, []);

  const requestReshaping = useCallback(
    (reshaping?: Reshaping) => {
      if (selectedActivity && reshaping) {
        setSelectedActivity(undefined);
      }
      setReshaping(reshaping);
    },
    [selectedActivity],
  );

  const setModalError = useCallback((errorMessage: string | ApiErrorParserBase | undefined) => {
    if (!errorMessage) {
      setModalErrorStr(undefined);
    } else if (typeof errorMessage === 'string') {
      setModalErrorStr(errorMessage);
    } else {
      setModalErrorStr(errorMessage.nonFieldErrorsStrings().join(', '));
    }
  }, []);

  useEffect(() => {
    if (dayParts) {
      if (timeScale === TimeScale.DayParts && dayParts.length <= 1) {
        setTimeScale(TimeScale.FullDay);
      }
    }
  }, [dayParts, timeScale]);

  return (
    <PlanningContext.Provider
      value={{
        viewType,
        timeScale,
        groupBy,
        fixedActivityType,
        apiPromises,
        activities,
        dailyNotes,
        dayParts,
        horses,
        publicHorses,
        contacts: fullOrShallowContacts,
        activityTypes,
        groups,
        stables,
        facilities,
        facilityEvents,
        roles,
        setLastUsedActivityTypeUid,
        historicOffset,
        setViewType,
        setTimeScale,
        setGroupBy,
        setApiPromises,
        setActivities,
        setDailyNotes,
        setDayParts,
        setHorses,
        setPublicHorses,
        setContacts,
        setContactsShallow,
        setActivityTypes,
        setGroups,
        setStables,
        setFacilities,
        setFacilityEvents,
        setRoles,
        updateContact,
        lastUsedActivityTypeUid,
        setHistoricOffset,
        processedActivities,
        bluePrint,
        requestBluePrint,
        reshaping,
        requestReshaping,
        selectedActivity,
        setSelectedActivity: setSelectedActivityObject,
        clearSelectedActivity,
        dragActivity,
        setDragActivity,
        selectedGroupBy,
        setSelectedGroupBy,
        modalError,
        setModalError,
        workingHours,
        setHideNonWorkingHours,
        hideNonWorkingHours,
        inspectActivitiesInModal,
        setInspectActivitiesInModal,
        clearInspectActivitiesInModal,
        filterHorseUid,
        workDayAvailabilityActivities,
        setWorkDayAvailabilityActivities,
        availabilities,
        setAvailabilities,
        workDayAvailabilityActivitiesManager,
        setWorkDayAvailabilityActivitiesManager,
      }}
    >
      {children}
    </PlanningContext.Provider>
  );
}
